'use client';

import { classes } from '@/uiPrimitives/base/classes.helpers';
import { box } from '@/uiPrimitives/layout/box';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { textStyle } from '@/uiPrimitives/typography/text';
import { addTestSelector } from '@/globals/testHelpers/testSelector';
import {
	accordionContentPadding,
	accordionItem,
	delayIconTransitionHover,
} from './Accordion.css';
import { icon } from '@/uiPrimitives/media/icon';
import { chevronIcon } from '@/uiIcons/chevronIcon.css';
import { Collapse } from '../Collapse';
import { ReactNode, useId } from 'react';
import {
	useAccordionItemOpened,
	useDispatchAccordion,
} from './AccordionProvider.client';
import { A11YAccordionActionLabel } from './helpers/A11YAccordionActionLabel';
import { focusBox } from '@/uiPrimitives/base/focus.css';
import { stack } from '@/uiPrimitives/layout/stack';
import { occupy } from '@/uiPrimitives/layout/occupy';

export type AccordionItemProps = {
	className?: string;
	openedClassName?: string;
	closedClassName?: string;
	/** Provide your own \<h2\>, \<h3\> or \<h4\> with your own `textBlock` */
	summarySlot: ReactNode;
	children: ReactNode;
	itemId: string | number;
	onToggle?: (props: { id: string | number; opening: boolean }) => void;
};

export const AccordionItem = ({
	className,
	openedClassName,
	closedClassName,
	summarySlot,
	children,
	itemId,
	onToggle,
}: AccordionItemProps) => {
	const htmlId = useId();
	const isOpened = useAccordionItemOpened(itemId);
	const dispatchAccordion = useDispatchAccordion();

	return (
		<div
			className={classes(
				className,
				accordionItem,
				isOpened ? openedClassName : closedClassName,
				stack({}),
			)}
		>
			<button
				aria-controls={htmlId}
				aria-expanded={isOpened}
				onClick={() => {
					dispatchAccordion({
						type: isOpened ? 'close' : 'open',
						id: itemId,
					});
					onToggle?.({
						id: itemId,
						opening: !isOpened,
					});
				}}
				className={classes(
					focusBox,
					shelf({
						gap: '-1 | small',
						gridTemplateColumns: 'minmax(0, 1fr) auto',
						alignItems: 'start',
					}),
					box({
						paddingBlock: '1 | h3',
						paddingInline: 'negative 1 | h3',
					}),
					textStyle({
						color: 'positiveBlue',
						fontWeight: isOpened ? 'bold' : undefined,
					}),
				)}
				{...addTestSelector(`uiAccordionItem`)}
			>
				{summarySlot}
				<A11YAccordionActionLabel isOpened={isOpened} />
				<span
					className={classes(
						occupy({ alignItems: 'center', blockSize: '0 | p' }),
					)}
				>
					<i
						className={classes(
							delayIconTransitionHover,
							icon({
								icon: chevronIcon,
								adjustIcon: 'rotate90deg',
								flip: isOpened ? 'horizontal:hover' : 'horizontalNone:hover',
								blockSize: '1ex',
							}),
						)}
					/>
				</span>
			</button>
			<Collapse id={htmlId} isOpened={isOpened}>
				<div
					className={classes(accordionContentPadding)}
					{...addTestSelector(`uiAccordionContent`)}
				>
					{children}
				</div>
			</Collapse>
		</div>
	);
};
